/**
 * PendencyList
 * Pendências devolvidas pela verificação ou pela prévia, separadas visualmente pelo que fazem:
 * o que impede a emissão vem primeiro, em vermelho; o que apenas avisa vem
 * depois. Sem essa separação, quem emite não sabe o que precisa resolver agora.
 *
 * Quando a pendência traz `action`, a lista mostra o CAMINHO da solução — sem
 * ele, avisos como "não tem ART válida cadastrada" são beco sem saída: quem
 * descobre o problema está aqui, e a tela que o resolve é de outra entidade.
 *
 * O link abre em OUTRA aba (#604): quem está aqui tem um formulário de emissão preenchido, e
 * trocar a página na mesma aba o perderia. Por isso é `href` de verdade, e não `RouterLink`,
 * que intercepta o clique.
 *
 * O link só aparece para quem tem a permissão que a API do destino exige. Sem
 * ela, sai o recado de pedir a um administrador — botão que responderia 403 é
 * pior do que botão nenhum.
 *
 * @version 2026.1.0
 */

import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { OpenInNew as AbreEmOutraAbaIcon } from '@mui/icons-material';
import { useAcao } from '../../../authorization/useVeredito';
import type { Pendency } from '../../../types/document';
import { CODIGO_DA_PENDENCIA_DE_DECLARACOES_EPI, ID_DO_PAINEL_DE_DECLARACOES_EPI } from './declaracoesEpi';

interface PendencyListProps {
  pendencies: Pendency[];
  /** Avisado quando a pessoa abre um link de resolver — a tela confere de novo na volta. */
  onAbrirLink?: () => void;
  /** Linha sob o título, como "Conferido agora". */
  subtitulo?: string;
}

/** O painel mora nesta mesma tela: rola até ele em vez de abrir outra aba e perder o formulário. */
const irParaOPainelDeDeclaracoes = (): void => {
  const painel = document.getElementById(ID_DO_PAINEL_DE_DECLARACOES_EPI);
  painel?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  painel?.focus({ preventScroll: true });
};

/**
 * Item isolado em componente porque é ele que consulta a permissão — e a
 * consulta é por pendência, já que cada ação declara a sua.
 */
const PendencyItem: React.FC<{ pendencia: Pendency; onAbrirLink?: () => void }> = ({
  pendencia,
  onAbrirLink,
}) => {
  const { action } = pendencia;
  const resolver = useAcao(action?.requiredPermission ?? '', action?.label ?? '');
  const levaAoPainel = pendencia.code === CODIGO_DA_PENDENCIA_DE_DECLARACOES_EPI;

  return (
    <ListItem disablePadding>
      <ListItemText
        primary={pendencia.message}
        secondary={
          <>
            {action !== undefined && !resolver.disabled && levaAoPainel && (
              <Link
                component="button"
                type="button"
                underline="always"
                onClick={irParaOPainelDeDeclaracoes}
                sx={{ verticalAlign: 'baseline' }}
              >
                {action.label}
              </Link>
            )}
            {action !== undefined && !resolver.disabled && !levaAoPainel && (
              <Link
                component="a"
                href={action.route}
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
                title="Abre em outra aba"
                onClick={onAbrirLink}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                {action.label}
                <AbreEmOutraAbaIcon sx={{ fontSize: 14 }} />
              </Link>
            )}
            {action !== undefined && resolver.disabled && (
              <Box component="span">{`${resolver.tooltip} — peça a um administrador.`}</Box>
            )}
          </>
        }
        slotProps={{ secondary: { component: 'div' } }}
      />
    </ListItem>
  );
};

const renderGrupo = (
  titulo: string,
  itens: Pendency[],
  severidade: 'error' | 'warning',
  onAbrirLink: (() => void) | undefined
): React.ReactNode => (
  <Alert severity={severidade} sx={{ mb: 2 }}>
    <AlertTitle>{titulo}</AlertTitle>
    <List dense disablePadding>
      {itens.map((pendencia) => (
        <PendencyItem
          key={`${pendencia.code}-${pendencia.sectionKey ?? ''}-${pendencia.message}`}
          pendencia={pendencia}
          onAbrirLink={onAbrirLink}
        />
      ))}
    </List>
  </Alert>
);

export const PendencyList: React.FC<PendencyListProps> = ({
  pendencies,
  onAbrirLink,
  subtitulo,
}) => {
  const bloqueantes = pendencies.filter((pendencia) => pendencia.severity === 'bloqueia');
  const avisos = pendencies.filter((pendencia) => pendencia.severity === 'avisa');

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle1">O que falta para emitir</Typography>
        {subtitulo !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {subtitulo}
          </Typography>
        )}
      </Box>

      {pendencies.length === 0 && (
        <Alert severity="success">Nenhuma pendência encontrada para esta emissão.</Alert>
      )}

      {bloqueantes.length > 0 &&
        renderGrupo('Impedem a emissão — resolva antes de gerar', bloqueantes, 'error', onAbrirLink)}

      {avisos.length > 0 && renderGrupo('Avisos — não impedem a emissão', avisos, 'warning', onAbrirLink)}
    </Box>
  );
};

export default PendencyList;
